///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Where the 3D NEXRAD volumes are drawing, the flat radar renderers (the
/// global mosaic in overlay_radar, its refinement tiles in webmercator_tile)
/// step back to a dim gray footprint so one location never carries two
/// encodings of the same reflectivity. NexradDatasetLayer publishes the
/// footprints of the volumes it drew each frame (an empty set while hidden),
/// which also advances the per-volume weights (a short fade as a volume
/// starts or stops drawing); the radar materials upload the result as the
/// radar_common.glsl `radarVolumeMask` / `radarVolumeMaskCount` uniforms.
///

import { clamp } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"

/** Ground footprint of one drawing volume */
export interface RadarVolumeFootprint {
	id: string
	/** Station longitude / latitude in radians */
	lon: number
	lat: number
	/** Half the side of the (square, tangent-plane) volume in km */
	halfSizeKm: number
}

/** Columns in the mat4 the shader loops over; NexradDatasetLayer caps active volumes at this many */
export const cMaxRadarVolumeFootprints = 4
// Wall-clock seconds for the composite to fade under a newly drawing volume (and back)
const cFadeSeconds = 0.5

interface WeightedFootprint {
	footprint: RadarVolumeFootprint
	weight: number
	drawing: boolean
}

export class RadarVolumeMask {
	private entries = new Map<string, WeightedFootprint>()
	private lastUpdateMs = 0
	// Column-major mat4: column i = (lon, lat, halfSizeKm, weight)
	private packed = new matrix(new Array<number>(16).fill(0))
	private packedCount = 0

	/** Drop every footprint at once (the publishing layer is going away) */
	public clear(): void {
		this.entries.clear()
		this.lastUpdateMs = 0
		this.packed.glArrayAccess().fill(0)
		this.packedCount = 0
	}

	/**
	 * Footprints of the volumes that drew this frame (replaces last frame's
	 * set), then advance the fades and repack the uniform values. Called
	 * once per rendered frame by the volume layer, hidden or not
	 */
	public publish(footprints: RadarVolumeFootprint[], nowMs: number): void {
		for (const entry of this.entries.values()) {
			entry.drawing = false
		}
		for (const footprint of footprints) {
			const entry = this.entries.get(footprint.id)
			if (entry) {
				entry.footprint = footprint
				entry.drawing = true
			} else {
				this.entries.set(footprint.id, { footprint, weight: 0, drawing: true })
			}
		}

		const dt = this.lastUpdateMs > 0 ? Math.min((nowMs - this.lastUpdateMs) / 1000, 0.1) : 0
		this.lastUpdateMs = nowMs
		const step = dt / cFadeSeconds

		// Strongest first so the ones that matter keep their column if more
		// than cMaxRadarVolumeFootprints are mid-fade at once
		const live: WeightedFootprint[] = []
		for (const [id, entry] of this.entries) {
			entry.weight = clamp(entry.weight + (entry.drawing ? step : -step), 0, 1)
			if (entry.weight <= 0 && !entry.drawing) {
				this.entries.delete(id)
			} else {
				live.push(entry)
			}
		}
		live.sort((a, b) => b.weight - a.weight)

		const values = this.packed.glArrayAccess()
		values.fill(0)
		const count = Math.min(live.length, cMaxRadarVolumeFootprints)
		for (let i = 0; i < count; i++) {
			const { footprint, weight } = live[i]
			values[i * 4] = footprint.lon
			values[i * 4 + 1] = footprint.lat
			values[i * 4 + 2] = footprint.halfSizeKm
			values[i * 4 + 3] = weight
		}
		this.packedCount = count
	}

	/** Uniform values for radar_common.glsl */
	public get mask(): matrix {
		return this.packed
	}

	public get count(): number {
		return this.packedCount
	}
}
